import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import type { ItemDTO } from '@central/types';
import { api } from '@/lib/api';

export interface SearchResultItem {
  item: ItemDTO;
  score: number;
  fts_rank?: number;
  semantic_score?: number;
  snippet: string;
  matched_in: string[];
}

export interface SearchResponse {
  query: string;
  tempoMs: number;
  total: number;
  grupos: Record<string, SearchResultItem[]>;
  resultados: SearchResultItem[];
  usouSemantico: boolean;
}

export interface SearchFiltersInput {
  tipo?: string;
  status?: string;
  tag?: string;
  mentorada_id?: number;
  desde?: string;
  limit?: number;
}

export function useSearch(query: string, filters: SearchFiltersInput = {}): UseQueryResult<SearchResponse> {
  const params = new URLSearchParams();
  params.set('q', query);
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '') params.set(k, String(v));
  }

  return useQuery({
    queryKey: ['search', query, filters],
    queryFn: () => api<SearchResponse>(`/api/search?${params.toString()}`),
    enabled: query.trim().length >= 2,
    staleTime: 10_000,
  });
}

export interface SearchHistoryEntry {
  id: number;
  query: string;
  filtros_json: string | null;
  criado_em: string;
}

export function useSearchHistory(): UseQueryResult<{ history: SearchHistoryEntry[] }> {
  return useQuery({
    queryKey: ['search', 'history'],
    queryFn: () => api<{ history: SearchHistoryEntry[] }>('/api/search/history'),
  });
}

export interface SavedSearch {
  id: number;
  nome: string;
  query: string;
  filtros_json: string | null;
  ordem: number;
  criado_em: string;
}

export function useSavedSearches(): UseQueryResult<{ saved: SavedSearch[] }> {
  return useQuery({
    queryKey: ['search', 'saved'],
    queryFn: () => api<{ saved: SavedSearch[] }>('/api/search/saved'),
  });
}

export function useSaveSearch(): ReturnType<
  typeof useMutation<SavedSearch, Error, { nome: string; query: string; filtros?: Record<string, unknown> }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => api<SavedSearch>('/api/search/saved', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['search', 'saved'] }),
  });
}
